/**
 * Module control (live) — the console half of the module control plane (core 0059).
 * The UI can't run DDL, so install/uninstall is a REQUEST: request_module() enqueues
 * it, the relay/installer-worker applies it, and we poll core.module_request for the
 * outcome, then re-read core.installed_modules() so the registry (and the nav) reflect
 * the real box. In fixture mode the Module Manager keeps its localStorage behaviour.
 */
import { useQuery } from '@tanstack/react-query'
import { useSolum } from './client'
import type { SolumClient } from '@solum/sdk'

export interface ModuleRequestRow { id: string; module: string; action: string; status: string; error: string | null }

export interface CustomModuleRow {
  id: string; key: string; name: string; account_id: string | null
  description: string | null; repo_url: string | null; current_version: string | null; status: string
}
/** Extensions — customer-coded modules (#927's ruled word; controlplane.custom_module, org-scoped RLS) — surfaced
 *  in the Module Manager (#14). Works in fixture + live; a customer sees only their own. */
export function useCustomModules() {
  const { client } = useSolum()
  return useQuery<CustomModuleRow[]>({
    queryKey: ['custom-modules'],
    queryFn: async () => (await client.raw().select<CustomModuleRow>('controlplane', 'custom_module'))
      .sort((a, b) => a.name.localeCompare(b.name)),
  })
}

/** Live installed modules from the box (core.installed_modules) — name + version. */
export function useLiveInstalledModules() {
  const { client, isLive } = useSolum()
  return useQuery<{ name: string; version: string }[]>({
    queryKey: ['installed-modules'],
    enabled: isLive,
    queryFn: () => client.raw().rpcTable<{ name: string; version: string }>('installed_modules', {}, 'core'),
  })
}

/** The marketplace's LIVE version per module (controlplane.marketplace_catalog) — the
 *  consumer side: what Moonbase has set live, so the Module Manager can flag updates. */
export function useMarketplaceCatalog() {
  const { client, isLive } = useSolum()
  return useQuery<Record<string, { live: string | null; status: string }>>({
    queryKey: ['marketplace-catalog'],
    enabled: isLive,
    queryFn: async () => Object.fromEntries((await client.raw().rpcTable<{ key: string; live_version: string | null; status: string }>('marketplace_catalog', {}, 'controlplane'))
      .map((r) => [r.key, { live: r.live_version, status: r.status }])),
  })
}

const cmpVer = (a: string, b: string) => {
  const pa = a.split('.').map(Number), pb = b.split('.').map(Number)
  for (let i = 0; i < 3; i++) { const d = (pa[i] || 0) - (pb[i] || 0); if (d) return d }
  return 0
}
/** true when the marketplace's live version is newer than what's installed. */
export const updateAvailable = (live: string | null | undefined, installed: string | null | undefined) =>
  !!live && !!installed && /^\d+\.\d+\.\d+$/.test(live) && /^\d+\.\d+\.\d+$/.test(installed) && cmpVer(live, installed) > 0

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** Enqueue an install/uninstall and poll to completion. Resolves { ok, error? }. */
export async function requestModule(
  client: SolumClient, module: string, action: 'install' | 'uninstall',
  opts: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<{ ok: boolean; error?: string }> {
  const timeoutMs = opts.timeoutMs ?? 90_000
  const intervalMs = opts.intervalMs ?? 1_500
  const id = await client.raw().rpcScalar<string>('request_module', { p_module: module, p_action: action }, 'core')
  const start = Date.now()
  for (;;) {
    const rows = await client.raw().select<ModuleRequestRow>('core', 'module_request', { where: { id } })
    const st = rows[0]?.status
    if (st === 'done') return { ok: true }
    if (st === 'failed') return { ok: false, error: rows[0]?.error ?? 'failed' }
    if (Date.now() - start > timeoutMs) return { ok: false, error: 'timed out — is the installer worker running?' }
    await sleep(intervalMs)
  }
}
